import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { fetchAppointmentById } from "../services/AppointmentService.js";
import { ThemeContext } from "../ThemeContext";

export default function AppointmentDetails() {
    const { id } = useParams();
    const [appointment, setAppointment] = useState(null);
    const theme = useContext(ThemeContext);

    useEffect(() => {
        async function load() {
            const data = await fetchAppointmentById(id);
            setAppointment(data);
        }
        load();
    }, [id]);

    if (!appointment)
        return (
            <p style={{ padding: 20, color: theme.text }}>Loading appointment...</p>
        );

    // Fields styling
    const fieldStyle = { display: "flex", flexDirection: "column", marginBottom: 15 };
    const labelStyle = { marginBottom: 5, fontWeight: "bold", color: theme.text };
    const inputStyle = {
        padding: "8px 10px",
        borderRadius: 5,
        border: `1px solid ${theme.darkMode ? "#555" : "#ccc"}`,
        backgroundColor: theme.cardBackground,
        color: theme.text,
    };
    const selectStyle = {
        ...inputStyle,
        cursor: "pointer",
    };

    const gridStyle = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 20,
    };

    const cardStyle = {
        backgroundColor: theme.cardBackground,
        padding: 20,
        borderRadius: 10,
        boxShadow: theme.darkMode
            ? "0 1px 6px rgba(0,0,0,0.5)"
            : "0 1px 6px rgba(0,0,0,0.1)",
    };

    const statusOptions = [
        { value: "pending", label: "Pending" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" },
        { value: "cancelled", label: "Cancelled" },
    ];


    return (
        <div
            style={{
                padding: 20,
                display: "flex",
                gap: 20,
                alignItems: "flex-start", // Align top
            }}
        >
            {/* ---------------- LEFT SIDE (3/5) ---------------- */}
            <div style={{ flex: 3 }}>
                <h2 style={{ marginBottom: 20, color: theme.text }}>
                    Appointment Details
                </h2>

                <div style={cardStyle}>
                    <div style={gridStyle}>
                        {/* ID */}
                        <div style={fieldStyle}>
                            <label style={labelStyle}>ID</label>
                            <input style={inputStyle} value={appointment.id} readOnly />
                        </div>

                        {/* Type */}
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Type</label>
                            <input style={inputStyle} value={appointment.type} readOnly />
                        </div>

                        {/* Service */}
                        <div style={fieldStyle}>
                            <label style={labelStyle}>Service</label>
                            <input style={inputStyle} value={appointment.questionnaire_response.questionnaire.title} readOnly />
                        </div>

                        {/* User */}
                        {appointment.supabase_user && (
                            <div style={fieldStyle}>
                                <label style={labelStyle}>User</label>
                                <input
                                    style={inputStyle}
                                    value={
                                        appointment.supabase_user.name
                                    }
                                    readOnly
                                />
                            </div>
                        )}

                        {/* Doctor */}
                        {appointment.doctor && (
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Doctor</label>
                                <input style={inputStyle} value={appointment.doctor.name} readOnly />
                            </div>
                        )}

                        {/* Clinic */}
                        {appointment.clinic && (
                            <div style={fieldStyle}>
                                <label style={labelStyle}>Clinic</label>
                                <input style={inputStyle} value={appointment.clinic.name} readOnly />
                            </div>
                        )}

                        {/* Questionnaire */}
                        {appointment.questionnaire_response?.length > 0 &&
                            appointment.questionnaire_response.map((q, index) => (
                                <div key={index} style={fieldStyle}>
                                    <label style={labelStyle}>
                                        {q.questionnaire?.title || `Questionnaire ${index + 1}`}
                                    </label>
                                    <input style={inputStyle} value={q.answer} readOnly />
                                </div>
                            ))}
                    </div>
                </div>

                {/* --- Questionnaire Response Card --- */}
                <div style={{ ...cardStyle, marginTop: 20 }}>
                    <h3 style={{ marginBottom: 20, color: theme.text }}>
                        Questionnaire Response
                    </h3>

                    {appointment.questionnaire_response &&
                        appointment.questionnaire_response.answer ? (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                                gap: "20px",
                            }}
                        >
                            {Object.entries(appointment.questionnaire_response.answer).map(
                                ([label, value]) => {
                                    const formatFileUrl = (file) => {
                                        if (!file) return "";
                                        if (file.startsWith("http")) return file; // already full URL
                                        return `https://dev.distincthealth.com${file}`;
                                    };

                                    return (
                                        <div
                                            key={label}
                                            style={{ display: "flex", flexDirection: "column" }}
                                        >
                                            {/* Label */}
                                            <label
                                                style={{
                                                    marginBottom: 6,
                                                    fontSize: 13,
                                                    color: theme.text,
                                                    opacity: 0.8,
                                                }}
                                            >
                                                {label}
                                            </label>

                                            {/* Value: If array → show file links */}
                                            {Array.isArray(value) ? (
                                                <div
                                                    style={{
                                                        backgroundColor: theme.inputBg,
                                                        border: `1px solid ${theme.darkMode ? "#555" : "#ccc"}`,
                                                        borderRadius: 8,
                                                        padding: "10px 12px",
                                                        color: theme.text,
                                                    }}
                                                >
                                                    {value.map((file, index) => {
                                                        const fullUrl = formatFileUrl(file);

                                                        return (
                                                            <a
                                                                key={index}
                                                                href={fullUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    display: "block",
                                                                    color: "#4EA8FF",
                                                                    textDecoration: "underline",
                                                                    marginBottom: 4,
                                                                    wordBreak: "break-all",
                                                                }}
                                                            >
                                                                {fullUrl}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <input
                                                    value={value}
                                                    readOnly
                                                    style={{
                                                        padding: "10px 12px",
                                                        borderRadius: 8,
                                                        border: `1px solid ${theme.darkMode ? "#555" : "#ccc"}`,
                                                        backgroundColor: theme.inputBg,
                                                        color: theme.text,
                                                        fontSize: 14,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    );
                                }
                            )}
                        </div>
                    ) : (
                        <p style={{ color: theme.text }}>No questionnaire data.</p>
                    )}
                </div>

            </div>

            {/* ---------------- RIGHT SIDE (2/5) ---------------- */}
            <div
                style={{
                    flex: 2,
                    position: "sticky",
                    top: 20,
                    maxHeight: "calc(100vh - 40px)",
                    overflowY: "auto",
                }}
            >
                <h2 style={{ marginBottom: 45, color: theme.text }}></h2>

                <div style={cardStyle}>
                    <h3 style={{ marginBottom: 20, color: theme.text }}>Actions</h3>

                    {/* STATUS DROPDOWN */}
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Status</label>
                        <select
                            style={selectStyle}
                            value={appointment.appointement_status}
                            onChange={(e) =>
                                setAppointment({
                                    ...appointment,
                                    appointement_status: e.target.value,
                                })
                            }
                        >
                            {statusOptions.map((s) => (
                                <option key={s.value} value={s.value}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        style={{
                            width: "100%",
                            padding: "10px 15px",
                            borderRadius: 6,
                            border: "none",
                            backgroundColor: theme.primaryButton,
                            color: "#fff",
                            fontWeight: "bold",
                            cursor: "pointer",
                            marginTop: 10,
                            marginBottom: 20,
                        }}
                        onClick={() => {
                            alert("Update API coming soon!");
                        }}
                    >
                        Update Appointment
                    </button>

                    <hr
                        style={{
                            border: "none",
                            borderTop: `1px solid ${theme.darkMode ? "#444" : "#ddd"}`,
                            margin: "20px 0",
                        }}
                    />

                    {/* ORDER INFO */}
                    <h3 style={{ marginBottom: 15, color: theme.text }}>
                        Order Information
                    </h3>

                    <p style={{ marginBottom: 10, color: theme.text }}>
                        <strong>Created At:</strong>
                        <br />
                        {new Date(appointment.createdAt).toLocaleString()}
                    </p>

                    <p style={{ marginBottom: 10, color: theme.text }}>
                        <strong>Last Updated:</strong>
                        <br />
                        {appointment.updatedAt
                            ? new Date(appointment.updatedAt).toLocaleString()
                            : "—"}
                    </p>
                </div>
            </div>

        </div>
    );
}
